import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import { Settings, CheckCircle, Warning, Error as ErrorIcon } from '@mui/icons-material';
import { UserEquipment, EQUIPMENT_TYPE_LABELS } from '../types/Equipment';
import { equipmentService } from '../services/equipmentService';
import { preferencesManager } from '../utils/preferencesManager';
import EquipmentTypeIcon from './EquipmentTypeIcon';

interface EquipmentSelectorProps {
  selectedEquipmentIds: string[] | undefined;
  onEquipmentChange: (equipmentIds: string[]) => void;
  onSave: () => Promise<void>;
  isSaving?: boolean;
}

const EquipmentSelector: React.FC<EquipmentSelectorProps> = ({
  selectedEquipmentIds,
  onEquipmentChange,
  onSave,
  isSaving = false,
}) => {
  const [userEquipment, setUserEquipment] = useState<UserEquipment[]>([]);
  const [defaultEquipmentIds, setDefaultEquipmentIds] = useState<string[]>([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialEquipmentIds, setInitialEquipmentIds] = useState<string[]>([]);
  const hasLoadedRef = useRef(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Load user's equipment and auto-attach default equipment
  useEffect(() => {
    // Only run once on mount
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadEquipment = async () => {
      const userId = preferencesManager.getUserId();
      if (!userId) return;

      try {
        setLoadingEquipment(true);
        const equipment = await equipmentService.getUserEquipment(userId);
        const activeEquipment = equipment.filter(e => e.isActive);

        setUserEquipment(activeEquipment);

        // Auto-attach equipment marked as default
        const defaultEquipmentIds = activeEquipment
          .filter(eq => eq.isDefault)
          .map(eq => eq.equipmentId);

        setDefaultEquipmentIds(defaultEquipmentIds);

        // Auto-attach default equipment ONLY for truly new dives (equipmentIds is undefined, not empty array)
        // undefined = never saved equipment data (new dive) -> auto-attach defaults
        // [] or any array = user explicitly set equipment (even if empty) -> respect that choice
        if (!hasInitialized) {
          // Only auto-attach if selectedEquipmentIds is undefined (not [] or any array)
          if (selectedEquipmentIds === undefined && defaultEquipmentIds.length > 0) {
            onEquipmentChange(defaultEquipmentIds);
          }
          setHasInitialized(true);
        }

        // Store initial selection for change tracking
        setInitialEquipmentIds(selectedEquipmentIds || []);
      } catch (error) {
        console.error('Failed to load equipment:', error);
      } finally {
        setLoadingEquipment(false);
      }
    };

    loadEquipment();
  }, [selectedEquipmentIds, onEquipmentChange, hasInitialized]);

  // Track if there are unsaved changes by comparing with initial state
  useEffect(() => {
    // Compare current selection with initial selection
    const currentIds = [...(selectedEquipmentIds || [])].sort();
    const initialIds = [...initialEquipmentIds].sort();
    const hasChanged = JSON.stringify(currentIds) !== JSON.stringify(initialIds);
    setHasChanges(hasChanged);
  }, [selectedEquipmentIds, initialEquipmentIds]);

  const handleToggleEquipment = (equipmentId: string) => {
    const currentIds = selectedEquipmentIds || [];
    const newIds = currentIds.includes(equipmentId)
      ? currentIds.filter(id => id !== equipmentId)
      : [...currentIds, equipmentId];

    onEquipmentChange(newIds);
  };

  const handleLoadDefaults = async () => {
    if (defaultEquipmentIds.length === 0) {
      return;
    }

    setLoadingDefault(true);
    onEquipmentChange(defaultEquipmentIds);
    setLoadingDefault(false);
  };

  const handleSaveAsDefault = async () => {
    const userId = preferencesManager.getUserId();
    if (!userId) return;

    const currentIds = selectedEquipmentIds || [];
    try {
      await equipmentService.saveDefaultEquipment({
        userId,
        equipmentIds: currentIds,
        updatedAt: new Date().toISOString(),
      });
      setDefaultEquipmentIds(currentIds);
      alert('Default equipment saved successfully!');
    } catch (error) {
      console.error('Failed to save default equipment:', error);
      alert('Failed to save default equipment. Please try again.');
    }
  };

  const handleSave = async () => {
    await onSave();
    // Update initial IDs to current selection after successful save
    setInitialEquipmentIds(selectedEquipmentIds || []);
  };

  // Calculate service status for equipment
  const getServiceStatus = (equipment: UserEquipment) => {
    if (!equipment.serviceIntervalDives || equipment.serviceIntervalDives === 0) {
      return { status: 'none', message: 'No service interval set', color: '#9e9e9e', progress: 0 };
    }

    // Calculate dives since last service
    const divesSinceService = equipment.diveCount - (equipment.divesAtLastService || 0);
    const progress = (divesSinceService / equipment.serviceIntervalDives) * 100;

    if (progress < 70) {
      return {
        status: 'good',
        message: `${divesSinceService}/${equipment.serviceIntervalDives} dives`,
        color: '#4caf50',
        progress,
      };
    } else if (progress < 90) {
      return {
        status: 'warning',
        message: `${divesSinceService}/${equipment.serviceIntervalDives} dives - Service soon`,
        color: '#ff9800',
        progress,
      };
    } else {
      return {
        status: 'critical',
        message: `${divesSinceService}/${equipment.serviceIntervalDives} dives - Service due!`,
        color: '#f44336',
        progress,
      };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'warning':
        return <Warning sx={{ fontSize: 16 }} />;
      case 'critical':
        return <ErrorIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  if (loadingEquipment) {
    return (
      <Box display="flex" alignItems="center" gap={2} py={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Loading equipment...
        </Typography>
      </Box>
    );
  }

  if (userEquipment.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          No equipment configured yet. Visit <strong>Options → Equipment</strong> to add your dive gear.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {defaultEquipmentIds.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleLoadDefaults}
            disabled={loadingDefault}
            startIcon={loadingDefault ? <CircularProgress size={16} /> : <Settings />}
          >
            Load Default Gear
          </Button>
        )}
        {(selectedEquipmentIds && selectedEquipmentIds.length > 0) && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleSaveAsDefault}
            startIcon={<Settings />}
          >
            Save as Default
          </Button>
        )}
        {hasChanges && (
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={isSaving}
            color="primary"
          >
            {isSaving ? 'Saving...' : 'Save Equipment'}
          </Button>
        )}
      </Box>

      {/* Equipment Grid */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {userEquipment.map((equipment) => {
          const isSelected = (selectedEquipmentIds || []).includes(equipment.equipmentId);
          const label = equipment.brand && equipment.model
            ? `${equipment.brand} ${equipment.model}`
            : EQUIPMENT_TYPE_LABELS[equipment.equipmentType as keyof typeof EQUIPMENT_TYPE_LABELS] || equipment.equipmentType;

          const serviceStatus = getServiceStatus(equipment);

          return (
            <Tooltip
              key={equipment.equipmentId}
              title={
                <Box>
                  <Typography variant="caption" display="block">
                    <strong>{label}</strong>
                  </Typography>
                  {equipment.serialNumber && (
                    <Typography variant="caption" display="block">
                      S/N: {equipment.serialNumber}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block">
                    Total dives: {equipment.diveCount}
                  </Typography>
                  {equipment.lastServiceDate && (
                    <Typography variant="caption" display="block">
                      Last service: {new Date(equipment.lastServiceDate).toLocaleDateString()}
                    </Typography>
                  )}
                  {serviceStatus.status !== 'none' && (
                    <Typography variant="caption" display="block" sx={{ color: serviceStatus.color, fontWeight: 'bold' }}>
                      {serviceStatus.message}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Paper
                elevation={isSelected ? 4 : 1}
                sx={{
                  p: 2,
                  minWidth: 180,
                  cursor: 'pointer',
                  border: isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
                onClick={() => handleToggleEquipment(equipment.equipmentId)}
              >
                {/* Equipment Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EquipmentTypeIcon type={equipment.equipmentType} size={24} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                      {equipment.brand || EQUIPMENT_TYPE_LABELS[equipment.equipmentType as keyof typeof EQUIPMENT_TYPE_LABELS]}
                    </Typography>
                    {equipment.model && (
                      <Typography variant="caption" color="textSecondary">
                        {equipment.model}
                      </Typography>
                    )}
                  </Box>
                  {isSelected && (
                    <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                  )}
                </Box>

                {/* Dive Count Badge */}
                <Box sx={{ mb: 1 }}>
                  <Chip
                    label={`${equipment.diveCount} dives`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Box>

                {/* Service Status Progress Bar */}
                {serviceStatus.status !== 'none' && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      {getStatusIcon(serviceStatus.status)}
                      <Typography variant="caption" sx={{ color: serviceStatus.color, fontWeight: 'medium', fontSize: '0.65rem' }}>
                        {serviceStatus.message}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(serviceStatus.progress, 100)}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: serviceStatus.color,
                        },
                      }}
                    />
                  </Box>
                )}

              </Paper>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default EquipmentSelector;
